import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Video, Download, BookMarked, Headphones, ExternalLink, Sprout } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { Article: FileText, Video, Download };

/* ----- EDITABLE: Recommended books ----- */
const recommendedBooks = [
  {
    title: "Atomic Habits",
    author: "James Clear",
    note: "The clearest book ever written on building small, daily habits that compound.",
    link: "https://www.amazon.com/Atomic-Habits-Proven-Build-Break/dp/0735211299",
    cover: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    title: "The Millionaire Real Estate Agent",
    author: "Gary Keller",
    note: "The book every Keller Williams agent reads first — and the one Tammy returns to most.",
    link: "https://www.amazon.com/Millionaire-Real-Estate-Agent-About/dp/0071444041",
    cover: "https://images.unsplash.com/photo-1589998059171-988d887df646?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    title: "The Coaching Habit",
    author: "Michael Bungay Stanier",
    note: "Seven questions that will change how you lead, coach, and even how you parent.",
    link: "https://www.amazon.com/Coaching-Habit-Less-Change-Forever/dp/0978440749",
    cover: "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    title: "Mindset",
    author: "Carol S. Dweck",
    note: "The foundational work on growth vs. fixed mindset. A must for anyone in coaching.",
    link: "https://www.amazon.com/Mindset-Psychology-Carol-S-Dweck/dp/0345472322",
    cover: "https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    title: "The Body Keeps the Score",
    author: "Bessel van der Kolk",
    note: "How story, trauma, and the body are connected. Essential reading for the work of healing.",
    link: "https://www.amazon.com/Body-Keeps-Score-Healing-Trauma/dp/0143127748",
    cover: "https://images.unsplash.com/photo-1535905557558-afc4877a26fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    title: "The Daily Stoic",
    author: "Ryan Holiday",
    note: "366 short meditations on Stoic wisdom — Tammy's morning companion for years.",
    link: "https://www.amazon.com/Daily-Stoic-Meditations-Wisdom-Perseverance/dp/0735211736",
    cover: "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
];

/* ----- EDITABLE: Recommended podcasts ----- */
const recommendedPodcasts = [
  {
    name: "Think Like a CEO",
    host: "Gary Keller",
    note: "Gary Keller's conversations with top business minds on how CEOs actually think and lead.",
    link: "https://kellerpodcastnetwork.com/thinklikeaceo",
  },
  {
    name: "The ONE Thing",
    host: "Jay Papasan",
    note: "Practical episodes on focus, productivity, and identifying the single most important thing.",
    link: "https://the1thing.com/podcast/",
  },
  {
    name: "Empire Building",
    host: "Keller Podcast Network",
    note: "Four powerhouse women on building businesses, families, and lives without burning out.",
    link: "https://kellerpodcastnetwork.com/empirebuilding",
  },
  {
    name: "Win Make Give",
    host: "Win Make Give Podcast Network",
    note: "The flagship show on building a life worth living — winning, making, and giving back.",
    link: "https://winmakegive.com/",
  },
  {
    name: "The Miracle Morning Podcast",
    host: "Hal Elrod",
    note: "The morning routine and mindset framework that has changed millions of lives, including Tammy's.",
    link: "https://www.miraclemorning.com/podcast/",
  },
];

export default function GrowYourMind() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/resources/public")
      .then((r) => setItems(r.data.resources || []))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="grow-your-mind-page">
      <Link
        to="/lifestyles"
        className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition"
        data-testid="grow-back"
      >
        <ArrowLeft size={14} /> Back to Lifestyles
      </Link>

      <div className="grid md:grid-cols-12 gap-12 mt-8 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
            <Sprout size={14} /> Lifestyles · Grow Your Mind
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            Grow your <span className="italic text-primary">mind.</span>
          </h1>
          <p className="text-foreground/70 mt-5 text-base md:text-lg leading-relaxed max-w-xl">
            Habits and the unglamorous daily practices that compound over years.
          </p>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/75 leading-relaxed">
          The books and podcasts Tammy actually returns to — for business,
          coaching, and the quieter work of becoming.
        </p>
      </div>

      {/* Recommended Books */}
      <section className="mb-24" data-testid="recommended-books">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <BookMarked size={12} /> Recommended Reading
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Books on Tammy's
              <br />
              <span className="italic text-primary">shelf.</span>
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
            A short, opinionated list — the books Tammy returns to most for
            business, coaching, and life.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendedBooks.map((b, i) => (
            <a
              key={b.title}
              href={b.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`book-card-${i}`}
              className="group bg-card border border-border rounded-2xl overflow-hidden card-lift flex"
            >
              <div className="w-1/3 bg-muted overflow-hidden shrink-0">
                <img
                  src={b.cover}
                  alt={b.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-xl leading-tight">{b.title}</h3>
                <p className="text-xs uppercase tracking-[0.18em] text-foreground/55 mt-1">
                  {b.author}
                </p>
                <p className="text-sm text-foreground/75 mt-3 leading-relaxed flex-1">
                  {b.note}
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm text-primary font-medium">
                  View on Amazon <ExternalLink size={12} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Recommended Podcasts */}
      <section className="mb-24" data-testid="recommended-podcasts">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Headphones size={12} /> In Tammy's Earbuds
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Podcasts <span className="italic text-primary">worth your commute.</span>
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
            The shows Tammy actually keeps in her rotation — for the car, the
            barn, and the morning walk.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {recommendedPodcasts.map((p, i) => (
            <a
              key={p.name}
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`podcast-card-${i}`}
              className="group bg-card border border-border rounded-2xl p-6 card-lift flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-primary/12 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition">
                <Headphones className="text-primary" size={22} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl leading-tight">{p.name}</h3>
                <p className="text-xs uppercase tracking-[0.18em] text-foreground/55 mt-1">
                  Hosted by {p.host}
                </p>
                <p className="text-sm text-foreground/75 mt-3 leading-relaxed">{p.note}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm text-primary font-medium">
                  Listen <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Free Library — articles / downloads / videos (only if populated) */}
      {items.length > 0 && (
        <section data-testid="free-library">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10 border-t border-border pt-16">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
                <FileText size={12} /> Free Library
              </div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                Articles, downloads <span className="italic text-primary">& tools.</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
              From Tammy's coaching curriculum — free to use, share, and adapt.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="resource-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`resource-filter-${c}`}
                className={`px-4 py-2 text-sm rounded-full border transition ${
                  filter === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-foreground/70 border-border hover:border-primary hover:text-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading && <p className="text-foreground/60" data-testid="resources-loading">Loading resources...</p>}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => {
              const Icon = ICONS[r.type] || FileText;
              const colSpan = i % 5 === 0 ? "lg:col-span-2" : "";
              return (
                <a
                  key={r.id}
                  href={r.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-card-${r.id}`}
                  className={`group bg-card border border-border rounded-2xl p-8 card-lift block ${colSpan}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="text-primary" size={28} strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">
                      {r.category}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl leading-snug">{r.title}</h3>
                  <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                    {r.summary}
                  </p>
                  <div className="flex items-center gap-2 mt-6 text-sm text-primary font-medium">
                    {r.type === "Download" ? "Download" : r.type === "Video" ? "Watch" : "Read"}
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
